/**
 * A short beep on a named device: the only way to answer "which of these six is
 * the headset?".
 *
 * A list of output devices is a list of driver names - "Speakers (Realtek(R)
 * Audio)", "Headset Earphone (Jabra 75)", and three virtual ones somebody's
 * conferencing software installed - and reading them tells an operator nothing
 * about which one their ear is next to. Twilio ships `speakerDevices.test()` for
 * exactly this reason; this is the same idea without needing a `Device` to
 * exist, so the check also works before the day's first call.
 *
 * It plays through the SHARED context, not one of its own, and that is what
 * makes the button worth pressing. A beep that came out of a context built for
 * the beep would prove that this browser can make a sound - which was never in
 * doubt - while saying nothing about the one sound that matters, the ring, which
 * plays through a context built at a completely different moment. Same context,
 * same sink, same volume: the test now answers the question it appears to.
 */
import { openAudioSink } from "./sink";
import { readyAudioContext } from "./context";

/** Two notes, a fifth apart, so it cannot be mistaken for the incoming ring. */
const NOTES = [660, 880];
const NOTE_SEC = 0.16;
const GAIN = 0.12;

/**
 * Play the beep and resolve when it has finished.
 *
 * Never rejects. It is called from a button whose only failure mode worth
 * reporting is silence, which the operator hears for themselves.
 *
 * `level` is the slider that sits above the button, and passing it is the point
 * of the button existing on the ring row at all: "is this loud enough" cannot
 * be answered by a test tone played at a different volume from the ring.
 */
export async function playTestTone(deviceId: string, level = 1): Promise<void> {
  // The click on the button is the gesture, so this is also the most reliable
  // moment in the whole app to get the shared context out of `suspended` - an
  // operator checking their headset before the day starts unlocks the ring for
  // the rest of the session without knowing they did.
  const ctx = await readyAudioContext();
  if (!ctx) return;

  let sink;
  try {
    sink = await openAudioSink(ctx, deviceId);
  } catch {
    return;
  }

  const peak = GAIN * Math.max(0, level);
  const start = ctx.currentTime + 0.02;
  NOTES.forEach((hz, i) => {
    const at = start + i * NOTE_SEC;
    const gain = ctx.createGain();
    gain.connect(sink.node);
    // Ramped at both ends: a square-edged gain change is an audible click, and
    // a click is the one artefact that would make a good speaker sound broken.
    gain.gain.setValueAtTime(0, at);
    gain.gain.linearRampToValueAtTime(peak, at + 0.02);
    gain.gain.setValueAtTime(peak, at + NOTE_SEC - 0.03);
    gain.gain.linearRampToValueAtTime(0, at + NOTE_SEC);

    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(hz, at);
    osc.connect(gain);
    osc.start(at);
    osc.stop(at + NOTE_SEC);
  });

  const totalMs = (NOTES.length * NOTE_SEC + 0.15) * 1000;
  await new Promise((resolve) => setTimeout(resolve, totalMs));
  // The sink goes, the context stays. See the ⚠ in `context.ts`: closing it
  // here would mean the operator silenced their own phone by testing it.
  sink.dispose();
}
